import { IconNames } from "@src/straps/base/icons/Icon/types";
import {
  ProjectBudgetStatus,
  ProjectStatus,
  ProjectTimelineStatus,
  ProjectType,
} from "@src/types";

export const projectStatusRenderingOptions: Record<
  ProjectStatus,
  {
    borderColorClass: string;
    iconColor: string; // Some of the required colors are not Semantic Colors, so setting icon color by setting the text color
    icon: IconNames;
    bgColor: string;
  }
> = {
  recommended: {
    borderColorClass: "border-gold",
    iconColor: "text-gold",
    icon: "recommended",
    bgColor: "bg-straps-warning-bg",
  },
  proposed: {
    borderColorClass: "border-[#CA7127]",
    iconColor: "text-[#CA7127]",
    icon: "proposed",
    bgColor: "bg-[#FCDDC4]",
  },
  planned: {
    borderColorClass: "border-charcoal500",
    iconColor: "text-charcoal500",
    icon: "planned",
    bgColor: "bg-straps-accent-3",
  },
  inProgress: {
    borderColorClass: "border-blue",
    iconColor: "text-blue",
    icon: "in-progress",
    bgColor: "bg-blue/25",
  },
  completed: {
    borderColorClass: "border-straps-positive",
    iconColor: "text-straps-positive",
    icon: "completed",
    bgColor: "bg-straps-positive-bg",
  },
};

// Renders a neutral chip instead of crashing if the API returns a status this build does not
// know about, e.g. the backend ships a new state before this frontend deploys.
const UNKNOWN_PROJECT_STATUS_RENDERING = {
  borderColorClass: "border-transparent",
  iconColor: "text-straps-tertiary",
  icon: "blank",
  bgColor: "bg-straps-accent-3",
} satisfies (typeof projectStatusRenderingOptions)[ProjectStatus];

export const isKnownProjectStatus = (status: string): status is ProjectStatus =>
  status in projectStatusRenderingOptions;

export const getProjectStatusRenderingOptions = (status: string) =>
  isKnownProjectStatus(status)
    ? projectStatusRenderingOptions[status]
    : UNKNOWN_PROJECT_STATUS_RENDERING;

export const projectCategoryRenderingValues: Record<
  ProjectType,
  { color: string; title: string; bgColor: string } // bgColor only used for legacy projects and can be removed once the redesign is released
> = {
  hvac: { title: "HVAC", color: "text-blue", bgColor: "bg-blue" },
  repairs_and_maintenance: {
    title: "Repairs & Maintenance",
    color: "text-green-kelly",
    bgColor: "bg-green-kelly",
  },
  renovation: {
    title: "Renovation",
    color: "text-orange",
    bgColor: "bg-orange",
  },
  ev_and_solar: {
    title: "EV + Solar",
    color: "text-yellow",
    bgColor: "bg-yellow",
  },
};

export const timelineStatusRenderingOptions: Record<
  ProjectTimelineStatus,
  { label: string; color: string; backgroundColor: string }
> = {
  on_time: {
    color: "text-straps-positive",
    label: "On Schedule",
    backgroundColor: "bg-straps-positive/25",
  },
  delayed: {
    color: "text-straps-negative",
    label: "Off Schedule",
    backgroundColor: "bg-straps-negative/25",
  },
  unknown: {
    color: "text-pure-white-300",
    label: "Unknown Timeline",
    backgroundColor: "bg-pure-white-300/25",
  },
};

export const budgetStatusRenderingOptions: Record<
  ProjectBudgetStatus,
  { label: string; color: string }
> = {
  within_budget: {
    color: "text-straps-positive",
    label: "On Budget",
  },
  over_budget: {
    color: "text-straps-negative",
    label: "Over Budget",
  },
  unknown: {
    color: "text-pure-white-300",
    label: "Unknown Budget",
  },
};
